import React, { createContext, useEffect, useState } from 'react';
import { buildFallbackBusinessStatus } from '../utils/businessHours';
import { fetchBusinessStatus } from '../services/businessStatus';

export const BusinessStatusContext = createContext({
    businessStatus: buildFallbackBusinessStatus(),
    businessStatusLoading: true,
    businessStatusError: null,
    refreshBusinessStatus: async () => buildFallbackBusinessStatus(),
});

export function BusinessStatusProvider({ children }) {
    const [businessStatus, setBusinessStatus] = useState(buildFallbackBusinessStatus());
    const [businessStatusLoading, setBusinessStatusLoading] = useState(true);
    const [businessStatusError, setBusinessStatusError] = useState(null);

    const loadBusinessStatus = async () => {
        try {
            const status = await fetchBusinessStatus();
            setBusinessStatus(status);
            setBusinessStatusError(null);
            return status;
        } catch (error) {
            const fallbackStatus = buildFallbackBusinessStatus();
            setBusinessStatus(fallbackStatus);
            setBusinessStatusError(error);
            return fallbackStatus;
        } finally {
            setBusinessStatusLoading(false);
        }
    };

    useEffect(() => {
        let isMounted = true;

        const refresh = async () => {
            const status = await fetchBusinessStatus();

            if (isMounted) {
                setBusinessStatus(status);
                setBusinessStatusError(null);
                setBusinessStatusLoading(false);
            }
        };

        refresh().catch((error) => {
            if (isMounted) {
                setBusinessStatus(buildFallbackBusinessStatus());
                setBusinessStatusError(error);
                setBusinessStatusLoading(false);
            }
        });

        const intervalId = setInterval(() => {
            refresh().catch((error) => {
                if (isMounted) {
                    setBusinessStatus(buildFallbackBusinessStatus());
                    setBusinessStatusError(error);
                }
            });
        }, 60000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, []);

    return (
        <BusinessStatusContext.Provider
            value={{
                businessStatus,
                businessStatusLoading,
                businessStatusError,
                refreshBusinessStatus: loadBusinessStatus,
            }}
        >
            {children}
        </BusinessStatusContext.Provider>
    );
}
